import type { KyInstance } from "ky";
import type {
    ApplicationStatus,
    AuthProvider,
    PractitionerLifecycle,
    ConfirmEmailVerificationRequest,
    ConfirmPasswordResetRequest,
    ConfirmPasswordResetResponse,
    GoogleExchangeRequest,
    MessageResponse,
    RequestPasswordResetRequest,
    RequestSignInCodeRequest,
    SetPasswordRequest,
    UserRole,
    VerifyPasswordResetRequest,
    VerifyPasswordResetResponse,
    VerifySignInCodeRequest,
} from "./index.js";
import { TURNSTILE_TOKEN_HEADER } from "./turnstile.js";

export type AccountType = "admin" | "practioner";

export type AuthIntent = "sign-in" | "sign-up";

export interface AuthResponse {
    accountType: AccountType;
    token: string;
    /** Present only when the flow also fires off a verification code as a side effect (sign-up). */
    otp?: OtpRequestResponse;
}

export interface OtpRequestResponse {
    message: string;
    retryAt?: string;
    locked?: boolean;
}

export interface SignUpDTO {
    email: string;
    password: string;
}

export interface SignInDTO {
    accountType: AccountType;
    email: string;
    password: string;
}

export interface GoogleAuthUrlResponse {
    url: string;
}

export interface AccountBase {
    id: string;
    email: string;
    isEmailVerified: boolean;
    identities: AuthProvider[];
    createdAt: string;
    updatedAt: string;
}

export interface AdminAccount extends AccountBase {
    accountType: "admin";
    role: UserRole;
    firstName?: string;
    lastName?: string;
}


export interface PractitionerProfileSummary {
    username: string;
    firstName: string;
    lastName: string;
    lifecycle: PractitionerLifecycle;
    /** Migrated from the old site; listed without a verified licence. */
    isLegacy: boolean;
    listedAt?: string;
    unlistedAt?: string;
    /** The latest expiry among `active` credentials. Absent while none is active. */
    licenceValidUntil?: string;
}

export interface PractionerAccount extends AccountBase {
    accountType: "practioner";
    profilePhotoUrl?: string;
    /** The most recent application. A fresh account always has a draft. */
    application?: {
        id: string;
        status: ApplicationStatus;
    };
    profile?: PractitionerProfileSummary;
}

export type AppAccount = AdminAccount | PractionerAccount;

export const isAdminAccount = (account: AppAccount): account is AdminAccount =>
    account.accountType === "admin";

export class AuthApi {
    constructor(private readonly httpClient: KyInstance) { }

    whoAmI = (): Promise<AppAccount> =>
        this.httpClient.get("auth/@me").json<AppAccount>();

    signUp = (body: SignUpDTO, turnstileToken: string): Promise<AuthResponse> =>
        this.httpClient
            .post("auth/sign-up", { json: body, headers: { [TURNSTILE_TOKEN_HEADER]: turnstileToken } })
            .json<AuthResponse>();

    private signIn = (body: SignInDTO, turnstileToken: string): Promise<AuthResponse> =>
        this.httpClient
            .post("auth/sign-in", { json: body, headers: { [TURNSTILE_TOKEN_HEADER]: turnstileToken } })
            .json<AuthResponse>();

    signInAsPractioner = (credentials: SignUpDTO, turnstileToken: string): Promise<AuthResponse> =>
        this.signIn({ ...credentials, accountType: "practioner" }, turnstileToken);

    signInAsStaff = (credentials: SignUpDTO, turnstileToken: string): Promise<AuthResponse> =>
        this.signIn({ ...credentials, accountType: "admin" }, turnstileToken);

    getAuthenticationUrl = (
        { accountType, intent }: {
            accountType: AccountType;
            intent: AuthIntent;
        },
    ): Promise<GoogleAuthUrlResponse> =>
        this.httpClient
            .get("auth/oauth/google", {
                searchParams: { accountType, intent },
            })
            .json<GoogleAuthUrlResponse>();

    googleExchange = (body: GoogleExchangeRequest): Promise<AuthResponse> =>
        this.httpClient
            .post("auth/google/exchange", { json: body })
            .json<AuthResponse>();

    requestEmailVerification = (): Promise<OtpRequestResponse> =>
        this.httpClient.post("auth/email-verification").json<OtpRequestResponse>();

    confirmEmailVerification = (
        body: ConfirmEmailVerificationRequest,
    ): Promise<AppAccount> =>
        this.httpClient
            .post("auth/email-verification/confirm", { json: body })
            .json<AppAccount>();

    requestPasswordReset = (
        body: RequestPasswordResetRequest,
    ): Promise<OtpRequestResponse> =>
        this.httpClient
            .post("auth/password-reset", { json: body })
            .json<OtpRequestResponse>();

    verifyPasswordReset = (
        body: VerifyPasswordResetRequest,
    ): Promise<VerifyPasswordResetResponse> =>
        this.httpClient
            .post("auth/password-reset/verify", { json: body })
            .json<VerifyPasswordResetResponse>();

    confirmPasswordReset = (
        body: ConfirmPasswordResetRequest,
    ): Promise<ConfirmPasswordResetResponse> =>
        this.httpClient
            .post("auth/password-reset/confirm", { json: body })
            .json<ConfirmPasswordResetResponse>();

    requestSignInCode = (body: RequestSignInCodeRequest): Promise<OtpRequestResponse> =>
        this.httpClient.post("auth/sign-in-code", { json: body }).json<OtpRequestResponse>();

    signInWithCode = (body: VerifySignInCodeRequest): Promise<AuthResponse> =>
        this.httpClient.post("auth/sign-in-code/verify", { json: body }).json<AuthResponse>();

    setPassword = (body: SetPasswordRequest): Promise<MessageResponse> =>
        this.httpClient.post("auth/password", { json: body }).json<MessageResponse>();
}
